import { DocumentType } from '@portal/models/enumerators/onboarding';
import OnboardingApi from '@portal/repositories/onboarding';
import PendingDocumentsApi from '@portal/repositories/pendingDocuments';
import { Dispatch } from 'redux';

import {
  SET_PENDING_DOCUMENTS_ERROR,
  SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
} from './actionTypes';
import {
  decreaseUploadLoading,
  increaseUploadLoading,
  LoadingAction,
} from './loading';

type SaveAndUploadAction = { type: string; payload: Error | boolean };
type SaveAndUploadDispatch = Dispatch<SaveAndUploadAction | LoadingAction>;

export type SaveAndUploadParams = {
  id: string;
  type: DocumentType;
  file: File | null;
  secondFile?: File | null;
  mimeType?: string;
  jwt?: string;
};

type SaveAndUploadParamsIdwall = {
  id: string;
  type: DocumentType;
  ocrToken: string;
  origin: number;
  pj: boolean;
};

export const saveAndUploadIdwall =
  ({ id, type, ocrToken, origin, pj }: SaveAndUploadParamsIdwall) =>
  async (dispatch: SaveAndUploadDispatch) => {
    try {
      dispatch(increaseUploadLoading());
      await PendingDocumentsApi.saveDocumentIdwall({
        id,
        documentType: type,
        ocrToken,
        origin,
        pj,
      });

      dispatch({
        type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
        payload: true,
      });
    } catch (error) {
      if (error instanceof Error) {
        dispatch({
          type: SET_PENDING_DOCUMENTS_ERROR,
          payload: error,
        });
        dispatch({
          type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
          payload: false,
        });
      }
    } finally {
      dispatch(decreaseUploadLoading());
    }
  };

export const saveAndUpload =
  ({ id, type, file, secondFile, mimeType, jwt }: SaveAndUploadParams) =>
  async (dispatch: SaveAndUploadDispatch) => {
    dispatch(increaseUploadLoading());
    dispatch({ type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS, payload: false });
    try {
      const imageBinary = await file?.arrayBuffer();
      const isRg = type === DocumentType.RG;
      let secondBucket: null | string = null,
        secondUrl: null | string = null,
        secondImageBinary: null | ArrayBuffer = null;

      const { bucket, url } = await OnboardingApi.getImageUrl(
        file?.type.split('/')[1] ?? '',
        isRg ? DocumentType.RG_FRONT : type
      );

      if (secondFile) {
        const response = await OnboardingApi.getImageUrl(
          secondFile.name.split('.')[1],
          isRg ? DocumentType.RG_BACK : type
        );

        secondBucket = response.bucket;
        secondUrl = response.url;
        secondImageBinary = await secondFile.arrayBuffer();
      }

      await Promise.all([
        PendingDocumentsApi.saveDocument({
          id,
          type,
          document: bucket,
          documentBack: secondBucket ?? undefined,
          jwt,
        }),
        PendingDocumentsApi.uploadPicture(
          Buffer.from(imageBinary ? new Uint8Array(imageBinary) : ''),
          url,
          mimeType ?? file?.type ?? ''
        ),
        ...(secondUrl && secondImageBinary
          ? [
              PendingDocumentsApi.uploadPicture(
                Buffer.from(secondImageBinary),
                secondUrl,
                mimeType ?? 'image/jpeg'
              ),
            ]
          : []),
      ]);
      dispatch({
        type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
        payload: true,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch({
          type: SET_PENDING_DOCUMENTS_ERROR,
          payload: err,
        });
        dispatch({
          type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
          payload: false,
        });
      }
    } finally {
      dispatch(decreaseUploadLoading());
      dispatch({
        type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
        payload: false,
      });
    }
  };

export type SaveValueParams = {
  id: string;
  type: DocumentType;
  value: string;
  address?: undefined;
};

export type SaveAddressParams = {
  id: string;
  type: DocumentType.ADDRESS;
  address: models.Address;
  value?: undefined;
};

export const save =
  ({ id, type, value, address }: SaveAddressParams | SaveValueParams) =>
  async (dispatch: SaveAndUploadDispatch) => {
    dispatch(increaseUploadLoading());
    dispatch({ type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS, payload: false });
    try {
      await PendingDocumentsApi.saveDocument({
        id,
        type,
        document: value,
        address: address,
      });

      dispatch({
        type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
        payload: true,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch({
          type: SET_PENDING_DOCUMENTS_ERROR,
          payload: err,
        });
        dispatch({
          type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
          payload: false,
        });
      }
    } finally {
      dispatch(decreaseUploadLoading());
      dispatch({
        type: SET_PENDING_DOCUMENTS_UPLOAD_SUCCESS,
        payload: false,
      });
    }
  };
